import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Empty, Input, InputNumber, Modal, Spin, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import { LockOutlined, CloseOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { posService } from '@/services/pos.service';
import { formatNumber } from '@/utils/format';
import { getApiError } from '@/utils/apiError';
import { mapDetailToPosVariant } from '../utils/mapVariantDetail';
import { computePosTotals, itemVatAmount, priceIncludingVat } from '../utils/orderItemCalc';
import { applyB2BSuggestToOrderItems } from '../utils/suggestPosB2BPrices';
import { addComboToCart, findComboShortages } from '../utils/addComboToCart';
import {
  applyQuickAddEdits,
  buildQuickAddBaseItem,
  getQuickAddCartInfo,
  getQuickAddLock,
  isDiscountEdited,
  isPriceEdited,
  isQuickAddSerialRejected,
  isVatEdited,
  parseQuickAddList,
  type QuickAddComboPick,
  type QuickAddEdits,
  type QuickAddEntry,
  type QuickAddLock,
} from '../utils/quickAddItems';
import type {
  FulfillmentMode,
  OrderItem,
  PosComboSearchItem,
  PosVariant,
  PosVariantDetailItem,
  PosVariantSearchItem,
  PosWarehouseStock,
} from '../types';

/**
 * TCR-2303 — popup "Thêm nhanh hàng hóa" cho màn lên đơn B2B (thay nút "Nhập DS" khi `isB2B`).
 *
 * Hai cách vào (dán danh sách / tick từ lưới) cùng đổ vào MỘT bảng xem trước. Chưa có gì vào giỏ
 * cho tới khi bấm "Thêm N mã vào đơn" — lúc đó `onConfirm` nhận cả lô và màn cha ghi giỏ đúng 1
 * lần qua `mergeQuickAddIntoCart`.
 *
 * TCR-2407 — combo tick từ lưới đi đường RIÊNG: combo là ComboDefinition loại VIRTUAL (không phải
 * variant nên không tra được bằng `combo_sku`), nên tick = tra TỪNG THÀNH PHẦN qua
 * `lookupVariants` + kiểm thiếu tồn bằng `findComboShortages`, giữ lại thành pick trong khung
 * "Combo đã chọn" dưới lưới. Xác nhận thì cha gọi `appendComboPicksToCart` (bọc `addComboToCart`
 * của màn chính) — KHÔNG qua `mergeQuickAddIntoCart` vì đường dòng phẳng sẽ mất nhóm combo.
 *
 * Mọi dòng xem trước đều là `applyQuickAddEdits(defaults, SL, edits)` — cùng hàm sẽ dựng dòng giỏ
 * — nên số trong popup bằng số trong giỏ. `defaults` đi qua `applyB2BSuggestToOrderItems` (đường
 * gợi ý giá B2B của POS), tính lại khi SL đổi vì cấp giá có thể nhảy theo SL.
 */

type RowSource = 'paste' | 'grid';

/**
 * TCR-2303 — một thẻ trong lưới tab "Chọn từ lưới sản phẩm". Mặc định chỉ có
 * biến thể; khi đang tìm kiếm (có `q`) server trả thêm combo thật nên lưới vẽ
 * cả 2 loại. TCR-2407 — combo tick KHÔNG qua `addCodes` nữa (xem `toggleGridCombo`).
 */
type GridCard =
  | { kind: 'variant'; item: PosVariantSearchItem }
  | { kind: 'combo'; item: PosComboSearchItem };

interface BreakdownState {
  loading: boolean;
  items?: PosWarehouseStock[];
  error?: string;
}

interface Row {
  /** `variant_id` — mỗi biến thể đúng 1 dòng trong popup (mã trùng đã cộng dồn SL). */
  key: string;
  source: RowSource;
  variant: PosVariant;
  /** Tồn khả dụng theo chi nhánh từ `lookupVariants.stock_quantity`. */
  stock: number;
  quantity: number;
  defaults: OrderItem;
  /** SL mà `defaults` được tính cho — lệch `quantity` nghĩa là đang chờ tính lại cấp giá. */
  defaultsQty: number;
  edits: QuickAddEdits;
  lock: QuickAddLock;
  breakdownOpen: boolean;
  breakdown?: BreakdownState;
}

export interface QuickAddItemsModalProps {
  open: boolean;
  onClose: () => void;
  fulfillmentMode: FulfillmentMode;
  /** Chi nhánh của ca — dùng cho lưới sản phẩm và tồn theo kho. */
  storeId?: string;
  /** Khách đang chọn — gợi ý giá B2B theo khách. */
  customerId?: string;
  /** Dòng phụ tiêu đề: chi nhánh · khách. */
  subtitle?: string;
  discountPolicyEnabled?: boolean;
  /** Quyền `pos.discount` — cùng điều kiện khóa ô giá/CK ở `OrderItemsTable`. */
  hasDiscountPermission: boolean;
  /** Giỏ hiện tại — để cảnh báo vượt tồn và "đã có N trong đơn". */
  cartItems: OrderItem[];
  /**
   * TCR-2407 — tham số 2 là các combo đã tick từ lưới (đã qua lookup thành phần
   * + kiểm thiếu tồn). Cha thêm chúng vào giỏ bằng đúng hàm cụm combo của POS
   * (`appendComboPicksToCart`) — không qua đường dòng phẳng.
   */
  onConfirm: (entries: QuickAddEntry[], combos: QuickAddComboPick[]) => void;
}

const { Text } = Typography;
const QTY_DEBOUNCE_MS = 400;
const GRID_PAGE_SIZE = 40;
/** TCR-2303 — trễ giữa lúc gõ và lúc gọi server tìm lưới: mỗi ký tự không bắn request. */
const GRID_SEARCH_DEBOUNCE_MS = 300;

/** Khóa so khớp mã người dùng nhập với một kết quả lookup (mã SP, barcode, serial). */
function detailKeys(d: PosVariantDetailItem): string[] {
  const keys = [d.variant.variant_code, d.variant.barcode, d.stock_serial?.serial].filter(
    (k): k is string => typeof k === 'string' && k.length > 0,
  );
  if (d.stock_serial?.serial) keys.push(`${d.variant.variant_code}_${d.stock_serial.serial}`);
  return keys.map((k) => k.toLowerCase());
}

export function QuickAddItemsModal({
  open,
  onClose,
  fulfillmentMode,
  storeId,
  customerId,
  subtitle,
  discountPolicyEnabled,
  hasDiscountPermission,
  cartItems,
  onConfirm,
}: QuickAddItemsModalProps) {
  const [tab, setTab] = useState<'paste' | 'grid'>('paste');
  const [text, setText] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [checking, setChecking] = useState(false);

  // Lưới sản phẩm (tab 2) — tải lười lần đầu mở tab, phân trang "Tải thêm".
  // TCR-2303 — lưới giờ vẽ cả combo thật khi đang tìm (GridCard), và tìm kiếm
  // chạy trên server: `gridQuery` là chữ đang gõ, `gridSearch` là chữ ĐÃ áp
  // (debounce 300ms xong) mới được gửi đi. `gridSearch` rỗng = không tìm —
  // danh sách mặc định chỉ variants, không gọi combos/browse (giữ hành vi cũ).
  const [gridItems, setGridItems] = useState<GridCard[]>([]);
  const [gridPage, setGridPage] = useState(0);
  const [gridHasMore, setGridHasMore] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [gridError, setGridError] = useState<string | null>(null);
  const [gridQuery, setGridQuery] = useState('');
  const [gridSearch, setGridSearch] = useState('');
  // TCR-2407 — combo đã tick từ lưới (đã tra thành phần + kiểm thiếu tồn).
  const [comboPicks, setComboPicks] = useState<QuickAddComboPick[]>([]);
  const [comboLoadingId, setComboLoadingId] = useState<string | null>(null);
  // Ref song song với `comboLoadingId` — chặn tick đôi trong cùng 1 tick render
  // (closure của `toggleGridCombo` chưa kịp thấy state mới).
  const comboLoadingRef = useRef(false);
  // TCR-2303 — số thứ tự chặn race cho request tải lưới (khai báo sớm để effect
  // reset-đóng-modal ở trên tăng được nó).
  const gridLoadSeqRef = useRef(0);

  const isPreorder = fulfillmentMode === 'PREORDER';

  // TCR-2407 — phản chiếu `open` qua ref: các callback async (tick combo, tick
  // variant) sau khi `await` cần biết modal CÓ ĐÓNG trong lúc request bay hay
  // không — `open` trong closure là giá trị cũ lúc bấm, không dùng được.
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  // TCR-2407 — "phiên mở modal": tăng mỗi lần modal đóng. Request tra mã (`addCodes`)
  // chụp số này trước khi await; response về mà số đã đổi = modal đã đóng (có thể đã
  // mở lại) → kết quả thuộc phiên cũ, bỏ, không để rò vào state của lần mở mới.
  const sessionRef = useRef(0);

  // ---- reset khi đóng ----
  useEffect(() => {
    if (open) return;
    sessionRef.current += 1;
    // Nhả cờ "đang kiểm tra" của phiên cũ ngay lúc đóng (finally của request cũ không còn nhả hộ).
    setChecking(false);
    // TCR-2407 — đóng modal cũng phải VÔ HIỆU HÓA request lưới đang bay: tăng
    // sequence để response cũ về sau bị bỏ (state đã reset, ghi đè vào là rác),
    // và nhả cờ loading cho request không bao giờ về kịp.
    gridLoadSeqRef.current += 1;
    setGridLoading(false);
    setTab('paste');
    setText('');
    setRows([]);
    setErrors([]);
    setGridItems([]);
    setGridPage(0);
    setGridHasMore(false);
    setGridError(null);
    setGridQuery('');
    setGridSearch('');
    setComboPicks([]);
    setComboLoadingId(null);
    comboLoadingRef.current = false;
  }, [open]);

  // ---- dựng dòng mặc định (qua gợi ý giá B2B) ----
  const computeDefaults = useCallback(
    async (variant: PosVariant, quantity: number): Promise<OrderItem> => {
      const base = buildQuickAddBaseItem(variant, quantity, fulfillmentMode);
      try {
        const [suggested] = await applyB2BSuggestToOrderItems([base], fulfillmentMode, customerId, {
          trackPolicy: !!discountPolicyEnabled,
        });
        return suggested ?? base;
      } catch {
        // Gợi ý giá lỗi ⇒ dùng giá lookup; effect gợi ý của giỏ sẽ áp lại khi dòng vào đơn.
        return base;
      }
    },
    [fulfillmentMode, customerId, discountPolicyEnabled],
  );

  /**
   * Tra các mã qua `lookupVariants` rồi thêm/cộng dồn vào bảng xem trước. Dùng chung cho nút
   * "Kiểm tra mã" (dán) và tick lưới — một đường tra duy nhất nên tồn/giá/khóa luôn cùng nguồn.
   */
  const addCodes = useCallback(
    async (inputs: Array<{ code: string; quantity: number }>, source: RowSource): Promise<boolean> => {
      const session = sessionRef.current;
      const { items, item_errors } = await posService.lookupVariants(
        inputs.map((i) => i.code),
        fulfillmentMode,
      );
      // Modal đã đóng (hoặc đóng rồi mở lại) trong lúc request bay → response cũ, bỏ.
      // Phải chặn Ở ĐÂY (trước setRows/setErrors): guard ở nơi gọi chạy sau khi state đã bị ghi.
      if (session !== sessionRef.current || !openRef.current) return false;
      const newErrors: string[] = item_errors.map((e) => `${e.qr_code}${e.error ? ` (${e.error})` : ''}`);

      // Gom SL theo biến thể: nhiều mã (mã SP + barcode) có thể trỏ cùng 1 biến thể.
      const resolved = new Map<string, { detail: PosVariantDetailItem; quantity: number }>();
      const claimed = new Set<number>();
      for (const input of inputs) {
        const k = input.code.toLowerCase();
        const idx = items.findIndex((d) => detailKeys(d).includes(k));
        if (idx === -1) continue;
        claimed.add(idx);
        const d = items[idx];
        const cur = resolved.get(d.variant.variant_id);
        resolved.set(d.variant.variant_id, { detail: d, quantity: (cur?.quantity ?? 0) + input.quantity });
      }
      // Kết quả BE trả mà không khớp mã nào đã gõ (BE tự chuẩn hóa mã) → coi SL 1, không bỏ rơi.
      items.forEach((d, idx) => {
        if (claimed.has(idx) || resolved.has(d.variant.variant_id)) return;
        resolved.set(d.variant.variant_id, { detail: d, quantity: 1 });
      });

      const fresh: Row[] = [];
      for (const { detail, quantity } of resolved.values()) {
        const variant = mapDetailToPosVariant(detail);
        if (isQuickAddSerialRejected(variant, fulfillmentMode)) {
          newErrors.push(`${variant.variant_code} (hàng quản lý theo serial, không nhập theo lô được — thêm bằng ô tìm kiếm/quét mã)`);
          continue;
        }
        fresh.push({
          key: variant.variant_id,
          source,
          variant,
          stock: detail.stock_quantity ?? 0,
          quantity,
          defaults: buildQuickAddBaseItem(variant, quantity, fulfillmentMode),
          defaultsQty: -1, // chưa có gợi ý giá — effect tính lại bên dưới
          edits: {},
          lock: getQuickAddLock(variant),
          breakdownOpen: false,
        });
      }

      setRows((prev) => {
        const next = [...prev];
        for (const r of fresh) {
          const at = next.findIndex((x) => x.key === r.key);
          if (at === -1) next.push(r);
          else next[at] = { ...next[at], quantity: next[at].quantity + r.quantity };
        }
        return next;
      });
      setErrors((prev) => (source === 'paste' ? newErrors : [...prev, ...newErrors]));
      return true;
    },
    [fulfillmentMode],
  );

  // Tính lại giá mặc định cho dòng nào SL (hoặc lần đầu) chưa có gợi ý giá khớp. Debounce vì cấp
  // giá B2B có thể nhảy theo SL và mỗi lần là 1 request.
  const qtySignature = rows.map((r) => `${r.key}:${r.quantity}:${r.defaultsQty}`).join('|');
  useEffect(() => {
    const stale = rows.filter((r) => r.defaultsQty !== r.quantity);
    if (stale.length === 0) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      void Promise.all(
        stale.map(async (r) => ({ key: r.key, qty: r.quantity, defaults: await computeDefaults(r.variant, r.quantity) })),
      ).then((results) => {
        if (cancelled) return;
        setRows((prev) =>
          prev.map((r) => {
            const hit = results.find((x) => x.key === r.key && x.qty === r.quantity);
            return hit ? { ...r, defaults: hit.defaults, defaultsQty: hit.qty } : r;
          }),
        );
      });
    }, QTY_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `rows` đã được mã hoá trong qtySignature; computeDefaults ổn định theo mode/khách.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qtySignature, computeDefaults]);

  // Đổi khách / chế độ khi popup đang mở → giá mặc định phải tính lại.
  const ctxRef = useRef({ customerId, fulfillmentMode });
  useEffect(() => {
    if (ctxRef.current.customerId === customerId && ctxRef.current.fulfillmentMode === fulfillmentMode) return;
    ctxRef.current = { customerId, fulfillmentMode };
    setRows((prev) => prev.map((r) => ({ ...r, defaultsQty: -1 })));
  }, [customerId, fulfillmentMode]);

  const handleCheck = async () => {
    const { entries, invalidLines } = parseQuickAddList(text);
    const parseErrors = invalidLines.map((l) => `Dòng "${l}": số lượng phải là số nguyên dương`);
    if (entries.length === 0) {
      setErrors(parseErrors.length > 0 ? parseErrors : ['Chưa có mã nào. Dán mỗi dòng một mã kèm số lượng.']);
      return;
    }
    const session = sessionRef.current;
    setChecking(true);
    try {
      // Dán lại thay danh sách dán cũ, nhưng không đụng dòng đã tick từ lưới.
      setRows((prev) => prev.filter((r) => r.source !== 'paste'));
      const applied = await addCodes(entries.map((e) => ({ code: e.code, quantity: e.quantity })), 'paste');
      if (!applied) return; // response của phiên cũ — không ghi gì vào state phiên mới
      if (parseErrors.length > 0) setErrors((prev) => [...parseErrors, ...prev]);
    } catch (err) {
      if (session !== sessionRef.current) return;
      setErrors([getApiError(err)?.message ?? 'Không kiểm tra được mã. Thử lại sau.']);
    } finally {
      // Phiên cũ: effect đóng modal đã reset; đừng nhả cờ loading của lần kiểm tra mới.
      if (session === sessionRef.current) setChecking(false);
    }
  };

  // ---- lưới sản phẩm ----
  // TCR-2303 — tìm kiếm chạy TRÊN SERVER (BE tự bỏ dấu/hoa, khớp tên/SKU/
  // barcode/combo), lưới chỉ vẽ — bỏ hẳn lọc client. Số thứ tự `gridLoadSeq`
  // chặn race: response của một request cũ về SAU khi q đã đổi (hoặc đã đổi
  // tab / đóng modal — cả hai đều tăng sequence, xem effect reset và Tabs
  // onChange) thì bỏ im lặng, không đụng state (kể cả cờ loading — request
  // mới đang giữ nó).
  const loadGridPage = useCallback(
    async (page: number, q: string) => {
      const seq = ++gridLoadSeqRef.current;
      setGridLoading(true);
      setGridError(null);
      try {
        // Có q: variants + combos chạy SONG SONG cùng trang (combo thật chỉ
        // HIỂN THỊ trong lưới; lúc tick nó đi đường thành phần riêng — xem
        // `toggleGridCombo`). Không có q: CHỈ variants — giữ nguyên hành vi
        // cũ, không gọi combos/browse.
        const [variantsRes, combosRes] = await Promise.all([
          posService.browseVariants(fulfillmentMode, page, GRID_PAGE_SIZE, storeId, q || undefined),
          q ? posService.browseCombos(fulfillmentMode, page, GRID_PAGE_SIZE, q) : Promise.resolve(null),
        ]);
        if (seq !== gridLoadSeqRef.current) return; // q đã đổi — kết quả này đã lỗi thời
        const cards: GridCard[] = [
          ...variantsRes.items.map((item): GridCard => ({ kind: 'variant', item })),
          ...(combosRes?.items ?? []).map((item): GridCard => ({ kind: 'combo', item })),
        ];
        setGridItems((prev) => (page === 1 ? cards : [...prev, ...cards]));
        setGridPage(page);
        // Hai endpoint phân trang độc lập — còn "thêm" khi BẤT KỲ bên nào còn trang.
        setGridHasMore(variantsRes.has_more || (combosRes?.has_more ?? false));
      } catch (err) {
        if (seq !== gridLoadSeqRef.current) return;
        setGridError(getApiError(err)?.message ?? 'Không tải được danh sách sản phẩm');
      } finally {
        if (seq === gridLoadSeqRef.current) setGridLoading(false);
      }
    },
    [fulfillmentMode, storeId],
  );

  // Mở tab lần đầu HOẶC q áp dụng đổi (debounce xong, kể cả xoá về rỗng) →
  // luôn về trang 1. "Tải thêm" đi đường `loadGridPage(page + 1)` riêng, không
  // qua effect này nên không bị reset về đầu.
  useEffect(() => {
    if (open && tab === 'grid') void loadGridPage(1, gridSearch);
  }, [open, tab, gridSearch, loadGridPage]);

  // Debounce ô tìm lưới: gõ tiếp thì timer cũ bị huỷ (cleanup) — chỉ chữ cuối
  // cùng (đã trim) được áp. Chạy khi tab đang mở để mở tab không bắn request thừa.
  useEffect(() => {
    if (!open || tab !== 'grid') return;
    const timer = setTimeout(() => setGridSearch(gridQuery.trim()), GRID_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [gridQuery, open, tab]);

  const toggleGridItem = async (item: PosVariantSearchItem, checked: boolean) => {
    const variantId = item.variant.variant_id;
    if (!checked) {
      setRows((prev) => prev.filter((r) => r.key !== variantId));
      return;
    }
    const session = sessionRef.current;
    try {
      // TCR-2407 — modal đã đóng/mở lại trong lúc request bay: `addCodes` tự bỏ response
      // cũ (không ghi state), ở đây chỉ cần không báo lỗi thừa cho phiên mới.
      await addCodes([{ code: item.variant.variant_code, quantity: 1 }], 'grid');
    } catch (err) {
      if (!openRef.current || session !== sessionRef.current) return;
      setErrors((prev) => [...prev, getApiError(err)?.message ?? `Không thêm được ${item.variant.variant_code}`]);
    }
  };

  // TCR-2407 — tick combo đi đường THÀNH PHẦN, không phải đường `addCodes` với
  // mã combo: combo ở lưới là ComboDefinition loại VIRTUAL, KHÔNG phải variant,
  // nên BE không tra ra gì từ `combo_sku` (lỗi review Major). Đúng luồng như màn
  // chính (`usePosCartAdder.selectCombo`): tra từng `component_items[].variant_code`
  // → kiểm thiếu tồn thật bằng `findComboShortages` → giữ lại pick; lúc xác nhận
  // cha dùng `appendComboPicksToCart` (bọc `addComboToCart`) để vào giỏ thành cụm
  // dòng chung `comboGroupId` — KHÔNG tự chế logic giá combo.
  const toggleGridCombo = async (item: PosComboSearchItem, checked: boolean) => {
    if (!checked) {
      // Bỏ tick → gỡ CẢ CỤM (1 pick = 1 cụm thành phần, không phải từng dòng lẻ).
      setComboPicks((prev) => prev.filter((p) => p.combo.combo_id !== item.combo_id));
      return;
    }
    // Chặn tick đôi: một combo đang tra thì click thêm bị bỏ qua (ref vì closure
    // chưa kịp thấy state mới giữa 2 click liền tick).
    if (comboLoadingRef.current) return;
    comboLoadingRef.current = true;
    setComboLoadingId(item.combo_id);
    try {
      const codes = item.component_items.map((c) => c.variant_code);
      const { items, item_errors } = await posService.lookupVariants(codes, fulfillmentMode);
      // Đóng modal giữa chừng → mọi kết quả sau đây đều bỏ (state đã reset).
      if (!openRef.current) return;
      if (item_errors.length > 0) {
        setErrors((prev) => [
          ...prev,
          `${item.combo_name}: ${item_errors[0].error ?? 'Không tra được thành phần combo'}`,
        ]);
        return;
      }
      if (items.length < item.component_items.length) {
        setErrors((prev) => [...prev, `${item.combo_name}: combo có sản phẩm không còn bán được`]);
        return;
      }
      // Cổng tồn THẬT — số trên thẻ chỉ là cache 30s để vẽ nhãn (cùng lý do
      // `selectCombo` ở màn chính). Thiếu → báo rõ và KHÔNG thêm cụm.
      const shortages = findComboShortages(item, items, fulfillmentMode);
      if (shortages.length > 0) {
        setErrors((prev) => [
          ...prev,
          `Không đủ tồn cho combo "${item.combo_name}": ${shortages
            .map((s) => `${s.product_name} cần ${s.needed}, còn ${s.available}`)
            .join('; ')}`,
        ]);
        return;
      }
      setComboPicks((prev) => [
        ...prev.filter((p) => p.combo.combo_id !== item.combo_id),
        { combo: item, details: items, quantity: 1 },
      ]);
    } catch (err) {
      if (!openRef.current) return;
      setErrors((prev) => [...prev, getApiError(err)?.message ?? `Không thêm được combo ${item.combo_name}`]);
    } finally {
      comboLoadingRef.current = false;
      setComboLoadingId(null);
    }
  };

  // ---- sửa dòng ----
  const patchRow = (key: string, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const patchEdits = (key: string, patch: QuickAddEdits) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, edits: { ...r.edits, ...patch } } : r)));

  const toggleBreakdown = (row: Row) => {
    const open = !row.breakdownOpen;
    patchRow(row.key, { breakdownOpen: open });
    // Tải LƯỜI: chỉ khi bấm lần đầu, 1 request / 1 biến thể (không tải trước hàng loạt).
    if (!open || row.breakdown) return;
    patchRow(row.key, { breakdown: { loading: true } });
    posService
      .listVariantWarehouseStocks(row.variant.variant_id, storeId)
      .then((res) => patchRow(row.key, { breakdown: { loading: false, items: res.items } }))
      .catch((err) =>
        patchRow(row.key, {
          breakdown: { loading: false, error: getApiError(err)?.message ?? 'Không tải được tồn theo kho' },
        }),
      );
  };

  // ---- dòng hiệu lực + tổng ----
  const effective = useMemo(
    () => rows.map((r) => applyQuickAddEdits(r.defaults, r.quantity, r.edits, fulfillmentMode, r.lock)),
    [rows, fulfillmentMode],
  );

  // TCR-2407 — dựng sẵn dòng giỏ của từng combo đã tick bằng ĐÚNG `addComboToCart`
  // (hàm màn chính dùng) để tổng xem trước luôn bằng số giỏ — không tự chế công
  // thức giá combo. `lines` rỗng = dữ liệu combo hỏng (không đáng ra vì đã kiểm
  // lúc tick) → tổng ước tính theo `combo_price` đã chốt, VAT bỏ qua.
  const comboPreview = useMemo(
    () =>
      comboPicks.map((pick) => {
        try {
          const lines = addComboToCart([], pick.combo, pick.details, fulfillmentMode, uuidv4);
          return {
            pick,
            lines,
            net: lines.reduce((s, l) => s + l.totalAmount, 0),
            vat: lines.reduce((s, l) => s + itemVatAmount(l), 0),
          };
        } catch {
          return { pick, lines: [] as OrderItem[], net: pick.combo.combo_price, vat: 0 };
        }
      }),
    [comboPicks, fulfillmentMode],
  );
  // Mở rộng theo số combo — nhân mỗi dòng lên `quantity` lần, đúng hình ảnh giỏ
  // sau khi thêm N cụm.
  const comboLinesAll = useMemo(
    () => comboPreview.flatMap((x) => Array.from({ length: x.pick.quantity }, () => x.lines).flat()),
    [comboPreview],
  );

  const totals = useMemo(() => computePosTotals([...effective, ...comboLinesAll]), [effective, comboLinesAll]);
  const netTotal = useMemo(
    () => [...effective, ...comboLinesAll].reduce((s, i) => s + i.totalAmount, 0),
    [effective, comboLinesAll],
  );
  const pendingDefaults = rows.some((r) => r.defaultsQty !== r.quantity);

  // TCR-2407 — SL combo có thể tăng sau lúc tick: kiểm lại tồn thành phần (đã tra lúc
  // tick, lưu ở `pick.details`) theo qty × SL combo bằng CHÍNH `findComboShortages`
  // (PREORDER tự trả rỗng). Còn thiếu ⇒ báo ngay trong khung "Combo đã chọn" + khóa nút xác nhận.
  const comboShortageById = new Map(
    comboPicks.map((p) => [p.combo.combo_id, findComboShortages(p.combo, p.details, fulfillmentMode, p.quantity)]),
  );
  const hasComboShortage = [...comboShortageById.values()].some((s) => s.length > 0);

  const handleConfirm = () => {
    if (hasComboShortage) return;
    onConfirm(
      rows.map((r) => ({ variant: r.variant, quantity: r.quantity, defaults: r.defaults, edits: r.edits })),
      comboPicks,
    );
  };

  // ---- cột ----
  const columns = [
    {
      title: 'Sản phẩm',
      key: 'product',
      width: 280,
      render: (_: unknown, r: Row) => {
        const info = getQuickAddCartInfo(cartItems, r.variant.variant_id);
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{r.variant.variant_name}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{r.variant.variant_code}</Text>
            {info.mergeTargetQty !== undefined && (
              <div style={{ fontSize: 12, color: '#d48806' }} data-testid={`quick-add-merge-${r.key}`}>
                Đã có {info.mergeTargetQty} trong đơn, sẽ cộng dồn thành {info.mergeTargetQty + r.quantity}
              </div>
            )}
            {r.lock && (
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                <LockOutlined /> {r.lock === 'flash' ? 'Flash sale' : 'Combo'}: giá và chiết khấu bị khóa
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Tồn CN',
      key: 'stock',
      width: 130,
      align: 'right' as const,
      render: (_: unknown, r: Row) => {
        const info = getQuickAddCartInfo(cartItems, r.variant.variant_id);
        const needed = r.quantity + info.totalQty;
        // Đặt trước không có khái niệm tồn (lookup trả 0) — không cảnh báo, không hiện số giả.
        const over = !isPreorder && needed > r.stock;
        return (
          <div>
            {isPreorder ? (
              <Text type="secondary">—</Text>
            ) : (
              <span style={{ fontWeight: 700, color: over ? '#cf1322' : undefined }} data-testid={`quick-add-stock-${r.key}`}>
                {formatNumber(r.stock)}
              </span>
            )}
            {over && (
              <div style={{ fontSize: 12, color: '#cf1322' }} data-testid={`quick-add-over-${r.key}`}>
                Vượt tồn: cần {formatNumber(needed)}, còn {formatNumber(r.stock)}
              </div>
            )}
            {!isPreorder && (
              <div>
                <Button type="link" size="small" style={{ padding: 0, height: 'auto', fontSize: 12 }} onClick={() => toggleBreakdown(r)} data-testid={`quick-add-warehouse-link-${r.key}`}>
                  theo kho
                </Button>
                {r.breakdownOpen && (
                  <div style={{ fontSize: 12, color: '#6f6469', textAlign: 'right' }} data-testid={`quick-add-warehouse-${r.key}`}>
                    {r.breakdown?.loading && <Spin size="small" />}
                    {r.breakdown?.error && <span style={{ color: '#cf1322' }}>{r.breakdown.error}</span>}
                    {r.breakdown?.items?.length === 0 && <span>Không có tồn ở kho nào</span>}
                    {r.breakdown?.items?.map((w) => (
                      <div key={w.warehouse_id}>
                        {w.name}: <b>{formatNumber(w.sellable_available ?? w.available)}</b>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'SL',
      key: 'qty',
      width: 90,
      align: 'right' as const,
      render: (_: unknown, r: Row) => (
        <InputNumber
          min={1}
          precision={0}
          value={r.quantity}
          style={{ width: 76 }}
          aria-label="Số lượng"
          data-testid={`quick-add-qty-${r.key}`}
          onChange={(v) => v !== null && patchRow(r.key, { quantity: Math.max(1, Math.floor(Number(v))) })}
        />
      ),
    },
    {
      title: 'Đơn giá (chưa VAT)',
      key: 'price',
      width: 150,
      align: 'right' as const,
      render: (_: unknown, r: Row, i: number) => {
        const eff = effective[i];
        const locked = !hasDiscountPermission || !!r.lock;
        return (
          <div>
            <InputNumber
              min={0}
              value={eff.price}
              disabled={locked}
              style={{ width: 128 }}
              aria-label="Đơn giá"
              data-testid={`quick-add-price-${r.key}`}
              formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
              parser={(v) => Number(String(v ?? '').replace(/\./g, '')) as 0}
              onChange={(v) => patchEdits(r.key, { price: v === null ? undefined : Number(v) })}
            />
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
              {(eff.vat ?? 0) > 0 ? `${formatNumber(priceIncludingVat(eff))}đ gồm VAT` : ' '}
              {isPriceEdited(r.defaults, r.edits) && !r.lock && <b style={{ color: '#d48806' }}> · đã sửa</b>}
            </div>
          </div>
        );
      },
    },
    {
      title: 'CK %',
      key: 'ck',
      width: 90,
      align: 'right' as const,
      render: (_: unknown, r: Row, i: number) => (
        <div>
          <InputNumber
            min={0}
            max={100}
            value={effective[i].discountPercent}
            disabled={!hasDiscountPermission || !!r.lock}
            style={{ width: 72 }}
            aria-label="Chiết khấu phần trăm"
            data-testid={`quick-add-ck-${r.key}`}
            onChange={(v) => patchEdits(r.key, { discountPercent: v === null ? undefined : Number(v) })}
          />
          {isDiscountEdited(r.defaults, r.edits) && !r.lock && (
            <div style={{ fontSize: 11, color: '#d48806', fontWeight: 700 }}>đã sửa</div>
          )}
        </div>
      ),
    },
    {
      title: 'VAT %',
      key: 'vat',
      width: 90,
      align: 'right' as const,
      render: (_: unknown, r: Row, i: number) => (
        <div>
          <InputNumber
            min={0}
            max={100}
            value={effective[i].vat ?? 0}
            style={{ width: 72 }}
            aria-label="Thuế suất VAT"
            data-testid={`quick-add-vat-${r.key}`}
            onChange={(v) => patchEdits(r.key, { vat: v === null ? undefined : Number(v) })}
          />
          {isVatEdited(r.defaults, r.edits) && <div style={{ fontSize: 11, color: '#d48806', fontWeight: 700 }}>đã sửa</div>}
        </div>
      ),
    },
    {
      title: 'Thành tiền',
      key: 'total',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, __: Row, i: number) => (
        <div>
          <b data-testid={`quick-add-total-${rows[i].key}`}>{formatNumber(effective[i].totalAmount)}</b>
          {(effective[i].vat ?? 0) > 0 && (
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>VAT {formatNumber(itemVatAmount(effective[i]))}</div>
          )}
        </div>
      ),
    },
    {
      title: '',
      key: 'remove',
      width: 44,
      render: (_: unknown, r: Row) => (
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          aria-label="Bỏ dòng"
          data-testid={`quick-add-remove-${r.key}`}
          onClick={() => setRows((prev) => prev.filter((x) => x.key !== r.key))}
        />
      ),
    },
  ];

  const pickedKeys = new Set(rows.map((r) => r.key));

  // ---- thẻ lưới (variant / combo) ----
  const renderVariantCard = (it: PosVariantSearchItem) => {
    const id = it.variant.variant_id;
    const stock = it.stock_quantity;
    const isSerial = it.product.tracking_mode === 'serial' && !isPreorder;
    // Đặt trước không có tồn (BE trả null) → không coi là hết hàng.
    const outOfStock = !isPreorder && (stock ?? 0) <= 0;
    const disabled = isSerial || outOfStock;
    return (
      <div
        key={`${id}-${it.serial ?? ''}`}
        style={{ border: '1px solid #e6e1e3', borderRadius: 10, padding: 10, opacity: disabled ? 0.55 : 1 }}
      >
        <Checkbox
          checked={pickedKeys.has(id)}
          disabled={disabled}
          onChange={(e) => void toggleGridItem(it, e.target.checked)}
          data-testid={`quick-add-pick-${it.variant.variant_code}`}
        >
          <b style={{ fontSize: 13 }}>{it.product.name}</b>
        </Checkbox>
        <div><Text type="secondary" style={{ fontSize: 12 }}>{it.variant.variant_code}</Text></div>
        <div>
          {isSerial ? (
            <Tag>Hàng serial — dùng ô tìm kiếm</Tag>
          ) : outOfStock ? (
            <Tag color="red">Hết hàng</Tag>
          ) : (
            !isPreorder && <Tag color="green">Tồn {formatNumber(stock ?? 0)}</Tag>
          )}
        </div>
      </div>
    );
  };

  // TCR-2407 — thẻ combo: nhãn "Combo" nổi bật + tên/SKU combo. Chặn sớm 2
  // trường hợp không thêm theo lô được (cùng lý do `usePosCartAdder.selectCombo`
  // chặn ở màn chính): combo chứa hàng serial ở giao ngay (lookup đòi mã
  // `variant_code_serial` từng serial — mã combo không cung cấp được), và combo
  // hết lượt ở chi nhánh. `available_combo_qty` là số tham chiếu (cache 30s),
  // cổng chặn thật (tồn từng thành phần) chạy lúc tick qua `toggleGridCombo`.
  const renderComboCard = (it: PosComboSearchItem) => {
    const hasSerial = it.has_serial_component && !isPreorder;
    const outOfStock = !isPreorder && (it.available_combo_qty ?? 0) <= 0;
    const disabled = hasSerial || outOfStock;
    return (
      <div
        key={`combo-${it.combo_id}-${it.combo_sku}`}
        style={{ border: '1px solid #e6e1e3', borderRadius: 10, padding: 10, opacity: disabled ? 0.55 : 1 }}
      >
        <Checkbox
          checked={comboPicks.some((p) => p.combo.combo_id === it.combo_id)}
          disabled={disabled || comboLoadingId === it.combo_id}
          onChange={(e) => void toggleGridCombo(it, e.target.checked)}
          data-testid={`quick-add-pick-${it.combo_sku}`}
        >
          <b style={{ fontSize: 13 }}>{it.combo_name}</b>
        </Checkbox>
        <div><Text type="secondary" style={{ fontSize: 12 }}>{it.combo_sku}</Text></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          <Tag color="purple">Combo</Tag>
          {hasSerial ? (
            <Tag>Hàng serial — thêm từng SP rồi gộp combo</Tag>
          ) : outOfStock ? (
            <Tag color="red">Hết hàng</Tag>
          ) : (
            !isPreorder && it.available_combo_qty !== null && <Tag color="green">Tồn {formatNumber(it.available_combo_qty)}</Tag>
          )}
          {/* Đang tra thành phần — tick chưa chốt, cho spinner để NVBH không bấm lại */}
          {comboLoadingId === it.combo_id && <Spin size="small" data-testid={`quick-add-combo-loading-${it.combo_sku}`} />}
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1080}
      destroyOnHidden
      title={
        <div>
          <div>Thêm nhanh hàng hóa</div>
          {subtitle && <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>{subtitle}</Text>}
        </div>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 24 }} data-testid="quick-add-summary">
            <div><Text type="secondary" style={{ fontSize: 12 }}>Số mã</Text><div><b data-testid="quick-add-sum-count">{rows.length + comboPicks.length}</b></div></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>Tiền hàng (sau CK)</Text><div><b data-testid="quick-add-sum-net">{formatNumber(netTotal)}đ</b></div></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>Thuế VAT</Text><div><b data-testid="quick-add-sum-vat">{formatNumber(totals.totalVat)}đ</b></div></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>Tổng sau VAT</Text><div><b style={{ color: '#a4193d', fontSize: 16 }} data-testid="quick-add-sum-total">{formatNumber(totals.grandTotal)}đ</b></div></div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={onClose}>Hủy</Button>
            <Tooltip
              title={
                hasComboShortage
                  ? 'Có combo vượt tồn — giảm SL combo hoặc bỏ combo đó'
                  : pendingDefaults
                    ? 'Đang tính lại giá theo số lượng…'
                    : undefined
              }
            >
              <Button
                type="primary"
                disabled={(rows.length === 0 && comboPicks.length === 0) || pendingDefaults || hasComboShortage}
                onClick={handleConfirm}
                data-testid="quick-add-confirm"
              >
                {rows.length + comboPicks.length > 0
                  ? `Thêm ${rows.length + comboPicks.length} mã vào đơn`
                  : 'Thêm vào đơn'}
              </Button>
            </Tooltip>
          </div>
        </div>
      }
    >
      <Tabs
        activeKey={tab}
        onChange={(k) => {
          // TCR-2407 — rời/qua tab cũng vô hiệu hóa request lưới đang bay: quay
          // lại tab lưới luôn có effect tải lại trang 1, response cũ về giữa chừng
          // không còn ý nghĩa (cùng bộ đếm với đóng modal và đổi q).
          gridLoadSeqRef.current += 1;
          setTab(k as 'paste' | 'grid');
        }}
        items={[
          {
            key: 'paste',
            label: 'Dán danh sách',
            children: (
              <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
                <div style={{ flex: 1 }}>
                  <Input.TextArea
                    rows={4}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={'MVTP85WM1718\t20\nMVTP85AM1725\t10'}
                    aria-label="Danh sách mã và số lượng"
                    style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Mỗi dòng: <b>mã</b> và <b>số lượng</b>, cách nhau bằng tab, dấu phẩy hoặc khoảng trắng. Dán thẳng từ Excel được. Thiếu số lượng thì mặc định 1.
                  </Text>
                </div>
                <Button onClick={() => void handleCheck()} loading={checking} data-testid="quick-add-check">
                  Kiểm tra mã
                </Button>
              </div>
            ),
          },
          {
            key: 'grid',
            label: 'Chọn từ lưới sản phẩm',
            children: (
              <div data-testid="quick-add-grid">
                {/* TCR-2303 — tìm trên server (không lọc client): Enter không cần,
                    chữ được áp tự động sau debounce; nút tìm mặc định của
                    Input.Search bỏ vì không có hành động riêng. */}
                <Input
                  allowClear
                  placeholder="Tìm theo tên, SKU, barcode hoặc tên combo…"
                  value={gridQuery}
                  onChange={(e) => setGridQuery(e.target.value)}
                  style={{ marginBottom: 10, maxWidth: 360 }}
                  aria-label="Tìm trong lưới sản phẩm"
                />
                {gridError && <Alert type="error" showIcon message={gridError} style={{ marginBottom: 8 }} />}
                {gridLoading && gridItems.length === 0 ? (
                  // Đang tải/tìm mà chưa có gì để giữ nền — spinner đứng giữa cho rõ.
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }} data-testid="quick-add-grid-loading">
                    <Spin />
                  </div>
                ) : gridItems.length === 0 && !gridError ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={gridSearch ? `Không có sản phẩm khớp "${gridSearch}"` : 'Không có sản phẩm phù hợp'}
                  />
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10, maxHeight: 260, overflowY: 'auto' }}>
                    {gridItems.map((card) => (card.kind === 'variant' ? renderVariantCard(card.item) : renderComboCard(card.item)))}
                  </div>
                )}
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {gridLoading && gridItems.length > 0 && <Spin size="small" />}
                  {gridHasMore && !gridLoading && (
                    <Button size="small" onClick={() => void loadGridPage(gridPage + 1, gridSearch)}>Tải thêm</Button>
                  )}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Hàng hết tồn ở chi nhánh bị mờ và không tick được. Tick xong chỉnh SL và giá ở bảng bên dưới.
                  </Text>
                </div>
                {comboPicks.length > 0 && (
                  // TCR-2407 — combo đã tick hiển thị thành nhóm RIÊNG (không trộn
                  // vào bảng dòng phẳng phía dưới): nhãn Combo + tên/SKU, SL combo
                  // riêng, giá/CK khóa theo định mức. Xác nhận thì cả cụm vào giỏ
                  // qua `appendComboPicksToCart` — dòng phẳng không mang được nhóm.
                  <div style={{ marginTop: 10 }} data-testid="quick-add-combo-picks">
                    <div style={{ marginBottom: 4 }}>
                      <Text strong style={{ fontSize: 12 }}>Combo đã chọn</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}> — giá &amp; CK khóa theo định mức combo, chỉ đổi được số lượng.</Text>
                    </div>
                    <Table<QuickAddComboPick>
                      size="small"
                      rowKey={(p) => p.combo.combo_id}
                      pagination={false}
                      dataSource={comboPicks}
                      columns={[
                        {
                          title: 'Sản phẩm',
                          key: 'product',
                          render: (_: unknown, p: QuickAddComboPick) => (
                            <div>
                              <div>
                                <Tag color="purple">Combo</Tag>
                                <b>{p.combo.combo_name}</b>
                              </div>
                              <Text type="secondary" style={{ fontSize: 12 }}>{p.combo.combo_sku}</Text>
                              {(comboShortageById.get(p.combo.combo_id) ?? []).length > 0 && (
                                <div
                                  style={{ fontSize: 12, color: '#cf1322', marginTop: 2 }}
                                  role="alert"
                                  data-testid={`quick-add-combo-shortage-${p.combo.combo_sku}`}
                                >
                                  Không đủ tồn cho {p.quantity} combo:{' '}
                                  {(comboShortageById.get(p.combo.combo_id) ?? [])
                                    .map((sh) => `${sh.product_name} cần ${sh.needed}, còn ${sh.available}`)
                                    .join('; ')}
                                  . Giảm SL combo hoặc bỏ combo này.
                                </div>
                              )}
                            </div>
                          ),
                        },
                        {
                          title: 'SL combo',
                          key: 'qty',
                          width: 90,
                          align: 'right' as const,
                          render: (_: unknown, p: QuickAddComboPick) => (
                            <InputNumber
                              min={1}
                              precision={0}
                              value={p.quantity}
                              style={{ width: 76 }}
                              aria-label="Số lượng combo"
                              data-testid={`quick-add-combo-qty-${p.combo.combo_sku}`}
                              onChange={(v) =>
                                setComboPicks((prev) =>
                                  prev.map((x) =>
                                    x.combo.combo_id === p.combo.combo_id
                                      ? { ...x, quantity: Math.max(1, Math.floor(Number(v))) }
                                      : x,
                                  ),
                                )
                              }
                            />
                          ),
                        },
                        {
                          title: 'Giá combo (khóa)',
                          key: 'price',
                          width: 150,
                          align: 'right' as const,
                          render: (_: unknown, p: QuickAddComboPick) => (
                            <div>
                              <span style={{ color: '#8c8c8c' }}><LockOutlined /> </span>
                              <b>{formatNumber(p.combo.combo_price)}</b>
                              <div style={{ fontSize: 11, color: '#8c8c8c' }}>mỗi combo · giá &amp; CK khóa</div>
                            </div>
                          ),
                        },
                        {
                          title: 'Thành tiền',
                          key: 'total',
                          width: 130,
                          align: 'right' as const,
                          render: (_: unknown, p: QuickAddComboPick) => {
                            const hit = comboPreview.find((c) => c.pick.combo.combo_id === p.combo.combo_id);
                            const net = (hit?.net ?? p.combo.combo_price) * p.quantity;
                            const vat = (hit?.vat ?? 0) * p.quantity;
                            return (
                              <div>
                                <b>{formatNumber(net)}</b>
                                {vat > 0 && <div style={{ fontSize: 11, color: '#8c8c8c' }}>VAT {formatNumber(vat)}</div>}
                              </div>
                            );
                          },
                        },
                        {
                          title: '',
                          key: 'remove',
                          width: 44,
                          render: (_: unknown, p: QuickAddComboPick) => (
                            <Button
                              type="text"
                              size="small"
                              icon={<CloseOutlined />}
                              aria-label="Bỏ combo"
                              data-testid={`quick-add-combo-remove-${p.combo.combo_sku}`}
                              onClick={() =>
                                setComboPicks((prev) => prev.filter((x) => x.combo.combo_id !== p.combo.combo_id))
                              }
                            />
                          ),
                        },
                      ]}
                    />
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />

      {errors.length > 0 && (
        <Alert
          type="error"
          showIcon
          role="alert"
          style={{ marginBottom: 10 }}
          data-testid="quick-add-errors"
          message={`${errors.length} mã không thêm được`}
          description={
            <div>
              {errors.map((e, i) => (
                <div key={`${e}-${i}`}>{e}</div>
              ))}
              <div style={{ marginTop: 4 }}>Kiểm tra lại mã hoặc kênh bán. Các mã còn lại vẫn thêm được bình thường.</div>
            </div>
          }
        />
      )}

      <Table<Row>
        size="small"
        rowKey="key"
        pagination={false}
        dataSource={rows}
        columns={columns}
        scroll={{ x: 900, y: 320 }}
        onRow={(r) => {
          const info = getQuickAddCartInfo(cartItems, r.variant.variant_id);
          const over = !isPreorder && r.quantity + info.totalQty > r.stock;
          return over ? { style: { background: '#fff1f0' } } : {};
        }}
        locale={{ emptyText: 'Chưa có mã nào. Dán danh sách rồi bấm "Kiểm tra mã", hoặc tick sản phẩm từ lưới.' }}
      />
      {pendingDefaults && rows.length > 0 && (
        <Text type="secondary" style={{ fontSize: 12 }}>Đang tính lại giá theo số lượng…</Text>
      )}
    </Modal>
  );
}
